/**
 * taken from https://gist.github.com/codeguy/6684588?permalink_comment_id=3426313#gistcomment-3426313
 */
export const slugify = (string, separator = '-') => {
  if (!string) return '';
  return string
    .toString()
    .normalize('NFD') // split an accented letter in the base letter and the acent
    .replace(/[\u0300-\u036f]/g, '') // remove all previously split accents
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9 ]/g, '') // remove all chars not letters, numbers and spaces (to be replaced)
    .replace(/\s+/g, separator);
};

export const isBrowser = () => typeof window !== 'undefined';

const SCRIPTS_LOADED = {};
export const loadExternalScript = (src, options) => {
  const isScriptLoaded = SCRIPTS_LOADED[src];

  if (isScriptLoaded && !options?.force) {
    return isScriptLoaded;
  }

  // Remove previously loaded script element when forcing a reload
  if (options?.force) {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) existing.remove();
  }

  const promise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    if (options?.module) {
      script.type = 'text/partytown';
    }
    if (options?.defer) {
      script.defer = true;
    }
    script.src = src;
    script.onload = () => {
      resolve(true);
    };
    script.onerror = (error) => {
      reject(error);
    };
    if (options?.in === 'head') {
      document.head.appendChild(script);
    } else {
      document.body.appendChild(script);
    }
  });
  SCRIPTS_LOADED[src] = promise;
  return promise;
};

const PAGE_INTERACTION_EVENTS = ['pointerdown', 'touchstart', 'keydown', 'wheel', 'scroll'];
let hasPageInteracted = false;

const markPageInteraction = () => {
  hasPageInteracted = true;

  if (!isBrowser()) return;

  PAGE_INTERACTION_EVENTS.forEach((eventName) => {
    window.removeEventListener(eventName, markPageInteraction);
  });
};

if (isBrowser()) {
  PAGE_INTERACTION_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, markPageInteraction, { once: true, passive: true });
  });
}

export const onPageInteraction = (callback) => {
  if (!isBrowser()) return () => {};

  if (hasPageInteracted) {
    window.setTimeout(callback, 0);
    return () => {};
  }

  let cleanup = () => {};
  const handleInteraction = () => {
    cleanup();
    callback();
  };

  cleanup = () => {
    PAGE_INTERACTION_EVENTS.forEach((eventName) => {
      window.removeEventListener(eventName, handleInteraction);
    });
  };

  PAGE_INTERACTION_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, handleInteraction, { once: true, passive: true });
  });

  return cleanup;
};

export const scheduleNonCriticalTask = (callback, options = {}) => {
  if (!isBrowser() || typeof callback !== 'function') {
    return () => {};
  }

  const { timeout = 2500 } = options;
  let hasRun = false;
  let interactionCleanup = () => {};
  let idleHandle;

  const runOnce = () => {
    if (hasRun) return;
    hasRun = true;

    interactionCleanup();

    if (typeof window.cancelIdleCallback === 'function' && typeof idleHandle === 'number') {
      window.cancelIdleCallback(idleHandle);
    } else if (typeof idleHandle === 'number') {
      window.clearTimeout(idleHandle);
    }

    callback();
  };

  interactionCleanup = onPageInteraction(runOnce);

  if (typeof window.requestIdleCallback === 'function') {
    idleHandle = window.requestIdleCallback(runOnce, { timeout });
  } else {
    idleHandle = window.setTimeout(runOnce, timeout);
  }

  return () => {
    interactionCleanup();

    if (typeof window.cancelIdleCallback === 'function' && typeof idleHandle === 'number') {
      window.cancelIdleCallback(idleHandle);
    } else if (typeof idleHandle === 'number') {
      window.clearTimeout(idleHandle);
    }
  };
};

const PIPEDRIVE_LOADER_SRC = 'https://webforms.pipedrive.com/f/loader';
let hasLoadedPipedriveWebForms = false;
let scheduledPipedriveLoad = null;

export const loadPipedriveWebForms = () => {
  if (!isBrowser()) return Promise.resolve(false);

  if (scheduledPipedriveLoad) {
    return scheduledPipedriveLoad;
  }

  scheduledPipedriveLoad = new Promise((resolve, reject) => {
    window.setTimeout(() => {
      loadExternalScript(PIPEDRIVE_LOADER_SRC, {
        defer: true,
        in: 'head',
        force: hasLoadedPipedriveWebForms,
      })
        .then((value) => {
          hasLoadedPipedriveWebForms = true;
          resolve(value);
        })
        .catch(reject)
        .finally(() => {
          scheduledPipedriveLoad = null;
        });
    }, 0);
  });

  return scheduledPipedriveLoad;
};

export const elementIsVisibleInViewport = (el, partiallyVisible = false) => {
  const {
    top, left, bottom, right,
  } = el.getBoundingClientRect();
  const { innerHeight, innerWidth } = window;
  return partiallyVisible
    ? ((top > 0 && top < innerHeight)
        || (bottom > 0 && bottom < innerHeight))
        && ((left > 0 && left < innerWidth) || (right > 0 && right < innerWidth))
    : top >= 0 && left >= 0 && bottom <= innerHeight && right <= innerWidth;
};

export function deleteCookie(name) {
  if (!isBrowser()) return;
  document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
}

export function getCookie(cname) {
  if (!isBrowser()) return '';

  const name = `${cname}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return '';
}
